import type {
  StandardResponseDto,
  NotificationReadDto,
  NotificationFeedDto,
  NotificationQueryParams,
  NotificationType,
} from '@/types';
import type { AuthenticatedFetch } from '@/types/api';

// Re-export for convenience
export type { NotificationType };

/**
 * Fetch paginated notification feed
 */
export async function fetchNotifications(
  params: NotificationQueryParams,
  authenticatedFetch: AuthenticatedFetch
): Promise<StandardResponseDto<NotificationFeedDto>> {
  const query = new URLSearchParams();
  if (params.isRead !== undefined) query.set('isRead', String(params.isRead));
  if (params.category) query.set('category', params.category);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));

  const qs = query.toString();
  const data = await authenticatedFetch<StandardResponseDto<NotificationFeedDto>>(
    `notifications${qs ? `?${qs}` : ''}`,
    { method: 'GET' }
  );

  if (!data.success) {
    throw new Error('Failed to fetch notifications');
  }

  return data;
}

/**
 * Get unread notification count (for bell badge)
 * Returns the raw count value, handling both { count } and { unreadCount } response shapes.
 */
export async function fetchUnreadCount(
  authenticatedFetch: AuthenticatedFetch
): Promise<number> {
  // Backend returns { "count": 5 } — may or may not be wrapped in StandardResponseDto
  const data = await authenticatedFetch<Record<string, unknown>>(
    'notifications/unread-count',
    { method: 'GET' }
  );
  // Handle both wrapped ({ data: { count } }) and unwrapped ({ count }) shapes
  const inner = (data as Record<string, unknown>)?.data as Record<string, number> | undefined ?? data as Record<string, number>;
  return inner?.count ?? inner?.unreadCount ?? 0;
}

/**
 * Mark a single notification as read
 */
export async function markNotificationRead(
  id: string,
  authenticatedFetch: AuthenticatedFetch
): Promise<StandardResponseDto<NotificationReadDto>> {
  const data = await authenticatedFetch<StandardResponseDto<NotificationReadDto>>(
    `notifications/${id}/read`,
    { method: 'PATCH' }
  );

  if (!data.success) {
    throw new Error('Failed to mark notification as read');
  }

  return data;
}

/**
 * Mark all notifications as read.
 * Backend returns { "updated": N }.
 */
export async function markAllNotificationsRead(
  authenticatedFetch: AuthenticatedFetch
): Promise<void> {
  await authenticatedFetch<unknown>('notifications/read-all', { method: 'PATCH' });
}

/**
 * Dismiss (delete) a notification.
 * Backend returns { "success": true }.
 */
export async function dismissNotification(
  id: string,
  authenticatedFetch: AuthenticatedFetch
): Promise<void> {
  await authenticatedFetch<unknown>(`notifications/${id}`, { method: 'DELETE' });
}

// ============================================================================
// Push Notification Subscription Management
// ============================================================================

export interface PushSubscriptionDto {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent?: string;
  deviceType?: 'android' | 'ios' | 'desktop' | 'other';
}

export interface VapidPublicKeyDto {
  publicKey: string;
}

/**
 * Get the VAPID public key for push notifications
 */
export async function getVapidPublicKey(
  authenticatedFetch: AuthenticatedFetch
): Promise<string> {
  try {
    const data = await authenticatedFetch<StandardResponseDto<VapidPublicKeyDto>>(
      'notifications/push/public-key',
      { method: 'GET' }
    );

    if (!data.success || !data.data?.publicKey) {
      throw new Error('Failed to get VAPID public key');
    }

    return data.data.publicKey;
  } catch (error) {
    console.error('[Push] Failed to get VAPID public key:', error);
    throw error;
  }
}

/**
 * Subscribe to push notifications
 * Sends the push subscription to the backend
 */
export async function subscribeToPush(
  subscription: PushSubscriptionDto,
  authenticatedFetch: AuthenticatedFetch
): Promise<void> {
  try {
    const data = await authenticatedFetch<StandardResponseDto<unknown>>(
      'notifications/push/subscribe',
      {
        method: 'POST',
        body: JSON.stringify(subscription),
      }
    );

    if (!data.success) {
      throw new Error('Failed to subscribe to push notifications');
    }

    console.log('[Push] Successfully subscribed to push notifications');
  } catch (error) {
    console.error('[Push] Failed to subscribe to push notifications:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 * Removes the push subscription from the backend
 */
export async function unsubscribeFromPush(
  endpoint: string,
  authenticatedFetch: AuthenticatedFetch
): Promise<void> {
  try {
    const data = await authenticatedFetch<StandardResponseDto<unknown>>(
      'notifications/push/unsubscribe',
      {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      }
    );

    if (!data.success) {
      throw new Error('Failed to unsubscribe from push notifications');
    }

    console.log('[Push] Successfully unsubscribed from push notifications');
  } catch (error) {
    console.error('[Push] Failed to unsubscribe from push notifications:', error);
    throw error;
  }
}

/**
 * Check if user has an active push subscription
 */
export async function checkPushSubscription(
  authenticatedFetch: AuthenticatedFetch
): Promise<boolean> {
  try {
    const data = await authenticatedFetch<StandardResponseDto<{ subscribed: boolean }>>(
      'notifications/push/status',
      { method: 'GET' }
    );

    return data.data?.subscribed ?? false;
  } catch (error) {
    console.error('[Push] Failed to check push subscription status:', error);
    return false;
  }
}
